import {
  RealTimeCommunicationConnectionState,
  type RealTimeCommunicationDegradedReason,
  type RealTimeCommunicationStatus,
} from '@seech/core-ng';

export const RealtimeTransportState = {
  Idle: 'idle',
  Connecting: 'connecting',
  Connected: 'connected',
  Disconnected: 'disconnected',
} as const;

export type RealtimeTransportState =
  (typeof RealtimeTransportState)[keyof typeof RealtimeTransportState];

export interface RealtimeConnectionHealth {
  readonly state: RealTimeCommunicationConnectionState;
  readonly transportState: RealtimeTransportState;
  readonly isConnected: boolean;
  readonly message: string;
  readonly lastError: string | null;
  readonly degradedReason: RealTimeCommunicationDegradedReason | null;
  readonly hubUrl: string | null;
  readonly receiveMethod: string;
  readonly connectionId: string | null;
  readonly connectedOn: string | null;
  readonly disconnectedOn: string | null;
  readonly reconnectAttempt: number;
  readonly restartAttempt: number;
  readonly nextRestartOn: string | null;
  readonly activeRecipientCount: number;
  readonly activeReplayRequestCount: number;
  readonly activeListenerCount: number;
  readonly lastReplayOn: string | null;
  readonly lastConvergedOn: string | null;
}

export function buildRealtimeConnectionHealth(
  status: RealTimeCommunicationStatus,
  connectedMessage = 'Live updates connected.'
): RealtimeConnectionHealth {
  const isConnected = status.state === RealTimeCommunicationConnectionState.Connected;
  return {
    state: status.state,
    transportState: resolveRealtimeTransportState(status.state),
    isConnected,
    message: resolveRealtimeHealthMessage(status, connectedMessage),
    lastError: status.lastError,
    degradedReason: status.degradedReason,
    hubUrl: status.hubUrl,
    receiveMethod: status.receiveMethod,
    connectionId: status.connectionId,
    connectedOn: status.connectedOn,
    disconnectedOn: status.disconnectedOn,
    reconnectAttempt: status.reconnectAttempt,
    restartAttempt: status.restartAttempt,
    nextRestartOn: status.nextRestartOn,
    activeRecipientCount: status.activeRecipients.length,
    activeReplayRequestCount: status.activeReplayRequests.length,
    activeListenerCount: status.activeListenerCount,
    lastReplayOn: status.lastReplayOn,
    lastConvergedOn: status.lastConvergedOn,
  };
}

export function buildIdleRealtimeConnectionHealth(
  receiveMethod: string
): RealtimeConnectionHealth {
  return {
    state: RealTimeCommunicationConnectionState.Idle,
    transportState: RealtimeTransportState.Idle,
    isConnected: false,
    message: '',
    lastError: null,
    degradedReason: null,
    hubUrl: null,
    receiveMethod,
    connectionId: null,
    connectedOn: null,
    disconnectedOn: null,
    reconnectAttempt: 0,
    restartAttempt: 0,
    nextRestartOn: null,
    activeRecipientCount: 0,
    activeReplayRequestCount: 0,
    activeListenerCount: 0,
    lastReplayOn: null,
    lastConvergedOn: null,
  };
}

function resolveRealtimeTransportState(
  state: RealTimeCommunicationConnectionState
): RealtimeTransportState {
  switch (state) {
    case RealTimeCommunicationConnectionState.Connected:
      return RealtimeTransportState.Connected;
    case RealTimeCommunicationConnectionState.Connecting:
    case RealTimeCommunicationConnectionState.Synchronizing:
    case RealTimeCommunicationConnectionState.Reconnecting:
      return RealtimeTransportState.Connecting;
    case RealTimeCommunicationConnectionState.Disconnected:
    case RealTimeCommunicationConnectionState.Failed:
      return RealtimeTransportState.Disconnected;
    case RealTimeCommunicationConnectionState.Idle:
    default:
      return RealtimeTransportState.Idle;
  }
}

function resolveRealtimeHealthMessage(
  status: RealTimeCommunicationStatus,
  connectedMessage: string
): string {
  if (status.lastError && (
    status.state === RealTimeCommunicationConnectionState.Disconnected ||
    status.state === RealTimeCommunicationConnectionState.Failed
  )) {
    return status.lastError;
  }

  switch (status.state) {
    case RealTimeCommunicationConnectionState.Connected:
      return connectedMessage;
    case RealTimeCommunicationConnectionState.Connecting:
      return 'Connecting live updates...';
    case RealTimeCommunicationConnectionState.Synchronizing:
      return 'Synchronizing live updates...';
    case RealTimeCommunicationConnectionState.Reconnecting:
      return 'Reconnecting live updates...';
    case RealTimeCommunicationConnectionState.Disconnected:
      return 'Live updates disconnected.';
    case RealTimeCommunicationConnectionState.Failed:
      return 'Live updates failed.';
    case RealTimeCommunicationConnectionState.Idle:
    default:
      return '';
  }
}
